import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Trash2, Plus, Users, BookOpen, Brain, Search as SearchIcon } from "lucide-react";

export default function Admin() {
  const { authHeaders, user } = useAuth();
  const [stats, setStats] = useState({ users: 0, activities: 0, disorders: 0, searches: 0 });
  const [activities, setActivities] = useState([]);
  const [disorders, setDisorders] = useState([]);
  const [newAct, setNewAct] = useState({ name: "", description: "", emoji_icon: "sparkles" });
  const [newDis, setNewDis] = useState({ name: "", description: "" });

  const refresh = async () => {
    const [s, a, d] = await Promise.all([
      axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
      axios.get(`${API}/activities`),
      axios.get(`${API}/disorders`),
    ]);
    setStats(s.data);
    setActivities(a.data);
    setDisorders(d.data);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  if (user?.role !== "admin") {
    return <div className="max-w-4xl mx-auto px-6 py-20 text-center text-[#52606D]" data-testid="admin-denied">Admin access only.</div>;
  }

  const addActivity = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/activities`, newAct, { headers: authHeaders() });
      setNewAct({ name: "", description: "", emoji_icon: "sparkles" });
      toast.success("Activity added");
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const addDisorder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/disorders`, newDis, { headers: authHeaders() });
      setNewDis({ name: "", description: "" });
      toast.success("Disorder added");
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const delActivity = async (id) => {
    await axios.delete(`${API}/admin/activities/${id}`, { headers: authHeaders() });
    toast.success("Deleted");
    refresh();
  };

  const delDisorder = async (id) => {
    await axios.delete(`${API}/admin/disorders/${id}`, { headers: authHeaders() });
    toast.success("Deleted");
    refresh();
  };

  const statCards = [
    { label: "Users", v: stats.users, icon: Users },
    { label: "Activities", v: stats.activities, icon: BookOpen },
    { label: "Disorders", v: stats.disorders, icon: Brain },
    { label: "Searches", v: stats.searches, icon: SearchIcon },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="admin-page">
      <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Admin Panel</span>
      <h1 className="font-display text-4xl font-semibold text-[#2C4C3B] mt-1">Sensiiq Console</h1>
      <p className="text-[#52606D] mt-2">Manage activities, disorders and oversee platform usage.</p>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div key={s.label} className="p-5 rounded-2xl bg-white border border-[#E2E8F0]" data-testid={`admin-stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center gap-2 text-[#52606D] text-xs tracking-[0.15em] uppercase">
              <s.icon className="w-4 h-4" /> {s.label}
            </div>
            <div className="font-display text-3xl text-[#2C4C3B] mt-2">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-14 grid lg:grid-cols-2 gap-8">
        {/* Activities */}
        <section className="p-6 rounded-2xl bg-white border border-[#E2E8F0]">
          <h2 className="font-display text-2xl text-[#2C4C3B]">Activities</h2>
          <form onSubmit={addActivity} className="mt-4 space-y-3" data-testid="admin-add-activity-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Name</Label>
              <Input required value={newAct.name} onChange={(e) => setNewAct({ ...newAct, name: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-activity-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Description</Label>
              <Textarea required value={newAct.description} onChange={(e) => setNewAct({ ...newAct, description: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-activity-desc-input" />
            </div>
            <Button type="submit" className="btn-primary rounded-full" data-testid="admin-add-activity-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Activity
            </Button>
          </form>
          <ul className="mt-6 divide-y divide-[#E2E8F0]">
            {activities.map((a) => (
              <li key={a.id} className="py-3 flex items-center justify-between" data-testid={`admin-activity-${a.slug}`}>
                <div>
                  <div className="font-medium text-[#2C4C3B]">{a.name}</div>
                  <div className="text-xs text-[#52606D] line-clamp-1">{a.description}</div>
                </div>
                <button onClick={() => delActivity(a.id)} className="text-[#C66A51] hover:text-[#A85639] p-2" data-testid={`admin-del-activity-${a.slug}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Disorders */}
        <section className="p-6 rounded-2xl bg-white border border-[#E2E8F0]">
          <h2 className="font-display text-2xl text-[#2C4C3B]">Disorders</h2>
          <form onSubmit={addDisorder} className="mt-4 space-y-3" data-testid="admin-add-disorder-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Name</Label>
              <Input required value={newDis.name} onChange={(e) => setNewDis({ ...newDis, name: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-disorder-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#52606D]">Description</Label>
              <Textarea required value={newDis.description} onChange={(e) => setNewDis({ ...newDis, description: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-disorder-desc-input" />
            </div>
            <Button type="submit" className="btn-primary rounded-full" data-testid="admin-add-disorder-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Disorder
            </Button>
          </form>
          <ul className="mt-6 divide-y divide-[#E2E8F0]">
            {disorders.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between" data-testid={`admin-disorder-${d.slug}`}>
                <div>
                  <div className="font-medium text-[#2C4C3B]">{d.name}</div>
                  <div className="text-xs text-[#52606D] line-clamp-1">{d.description}</div>
                </div>
                <button onClick={() => delDisorder(d.id)} className="text-[#C66A51] hover:text-[#A85639] p-2" data-testid={`admin-del-disorder-${d.slug}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
